import { useAnalytics } from '@satellite/composables/useAnalytics';
import type { TrackOptions } from '@satellite/analytics/AnalyticsClient';
import {
  Events,
  type SideNavClickedPayload,
  type WarehouseSelectorClickedPayload,
  type HeliosEventsMap
} from '@/analytics/events';

export type TrackSideNavClickedOptions = TrackOptions<SideNavClickedPayload>;
export type TrackWarehouseSelectorClickedOptions = TrackOptions<WarehouseSelectorClickedPayload>;

function getScreenSize(): string {
  return `${window.innerWidth}x${window.innerHeight}`;
}

export function useNavigationAnalytics() {
  const analytics = useAnalytics<HeliosEventsMap>();

  function trackAddAppointmentClicked(
    navExpanded: boolean,
    options: TrackSideNavClickedOptions = {}
  ) {
    analytics.track(Events.NAVIGATION.ADD_APPOINTMENT_CLICKED, {
      screen_size: getScreenSize(),
      nav_expanded: navExpanded,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackSearchAppointmentsClicked(
    navExpanded: boolean,
    options: TrackSideNavClickedOptions = {}
  ) {
    analytics.track(Events.NAVIGATION.SEARCH_APPOINTMENTS_CLICKED, {
      screen_size: getScreenSize(),
      nav_expanded: navExpanded,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackWarehousesClicked(navExpanded: boolean, options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.WAREHOUSES_CLICKED, {
      screen_size: getScreenSize(),
      nav_expanded: navExpanded,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackMyOrganizationClicked(
    navExpanded: boolean,
    options: TrackSideNavClickedOptions = {}
  ) {
    analytics.track(Events.NAVIGATION.MY_ORGANIZATION_CLICKED, {
      screen_size: getScreenSize(),
      nav_expanded: navExpanded,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackUsersClicked(navExpanded: boolean, options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.USERS_CLICKED, {
      screen_size: getScreenSize(),
      nav_expanded: navExpanded,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackMyCarrierContactsClicked(
    navExpanded: boolean,
    options: TrackSideNavClickedOptions = {}
  ) {
    analytics.track(Events.NAVIGATION.MY_CARRIER_CONTACTS_CLICKED, {
      screen_size: getScreenSize(),
      nav_expanded: navExpanded,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackInsightsClicked(navExpanded: boolean, options: TrackSideNavClickedOptions = {}) {
    analytics.track(Events.NAVIGATION.INSIGHTS_CLICKED, {
      screen_size: getScreenSize(),
      nav_expanded: navExpanded,
      ...(options.additionalPayload ?? {})
    });
  }

  function trackWarehouseSelectorClicked(
    params: Omit<WarehouseSelectorClickedPayload, 'screen_size'>,
    options: TrackWarehouseSelectorClickedOptions = {}
  ) {
    analytics.track(Events.NAVIGATION.WAREHOUSE_SELECTOR_CLICKED, {
      screen_size: getScreenSize(),
      ...params,
      ...(options.additionalPayload ?? {})
    });
  }

  return {
    trackAddAppointmentClicked,
    trackSearchAppointmentsClicked,
    trackWarehousesClicked,
    trackMyOrganizationClicked,
    trackUsersClicked,
    trackMyCarrierContactsClicked,
    trackInsightsClicked,
    trackWarehouseSelectorClicked
  };
}
